// The day a signing package goes out, written the way a contract line
// wants it.
//
// CONSUMER: shared (the send path, the prep screen's preview, tests).
//
// Baker, 2026-09-16, on the Affiliated Business Arrangement Disclosure:
// "in the top right of the form, you can see a 'Date' line. This really
// needs to be a locked text box that populates the date the document is
// sent for signature, but that is not a field that I can pull from in
// the locked text box pull from record feature."
//
// TWO DECISIONS WORTH STATING.
//
// Arizona time, not the server's. A package sent at 6pm in Gilbert is
// already tomorrow in UTC, and a disclosure dated the day after the
// client received it is wrong in the one way a compliance officer
// notices. Arizona has no daylight saving, which is why a fixed offset
// is honest here and would not be anywhere else.
//
// The long form, "September 16, 2026", matching the date the OS stamps
// beside a signature. A contract carrying 9/16/26 in one box and
// September 16, 2026 in another reads as two systems, and the one thing
// this whole program is for is that it reads as one.

import { phoenixDateString } from "../scheduling/phoenix-time";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

/**
 * The Arizona calendar date of `now`, as a contract prints it.
 *
 * Built from the date parts rather than `toLocaleDateString` on
 * purpose: the Workers runtime and the browser do not guarantee the
 * same locale data, and this string goes onto a legal document, where
 * "the two of them usually agree" is not good enough.
 */
export function envelopeDateSent(now: Date = new Date()): string {
  const iso = phoenixDateString(now);
  const [y, m, d] = iso.split("-");
  const monthIndex = Number(m) - 1;
  const month = MONTHS[monthIndex];
  // Unreachable with a well-formed ISO date; falling back to the ISO
  // string rather than throwing, because a send must never fail over
  // the formatting of a date it already knows.
  if (!month || !y || !d) return iso;
  return `${month} ${Number(d)}, ${y}`;
}

/** Format a calendar date the way a contract prints it. */
function formatContractDate(year: number, monthIndex: number, day: number): string {
  const month = MONTHS[monthIndex];
  return month ? `${month} ${day}, ${year}` : `${year}-${monthIndex + 1}-${day}`;
}

/**
 * Read a date a person typed or the OS printed: "September 16, 2026",
 * "9/16/2026" or "2026-09-16". Null for anything else, never a guess.
 */
function parseContractDate(text: string): { y: number; m: number; d: number } | null {
  const t = text.trim();
  const long = /^([A-Za-z]+)\.?\s+(\d{1,2}),?\s+(\d{4})$/.exec(t);
  if (long) {
    const m = MONTHS.findIndex((name) => name.toLowerCase().startsWith((long[1] ?? "").toLowerCase().slice(0, 3)));
    if (m >= 0 && (long[1] ?? "").length >= 3) return { y: Number(long[3]), m, d: Number(long[2]) };
    return null;
  }
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
  if (iso) return { y: Number(iso[1]), m: Number(iso[2]) - 1, d: Number(iso[3]) };
  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(t);
  if (us) return { y: Number(us[3]), m: Number(us[1]) - 1, d: Number(us[2]) };
  return null;
}

/**
 * The same calendar day `months` later, as a contract prints it, or null
 * when the text is not a date we can read.
 *
 * DECISION: the day is clamped to the target month's length (August 31
 * plus six months is February 28, not March 3), because an agreement
 * that "expires March 3" when its term was meant to be six months is a
 * different contract. Pure calendar arithmetic, no time zone involved.
 */
export function addMonthsToContractDate(text: string, months: number): string | null {
  const parsed = parseContractDate(text);
  if (!parsed || parsed.m < 0 || parsed.m > 11 || parsed.d < 1 || parsed.d > 31) return null;
  const total = parsed.m + months;
  const year = parsed.y + Math.floor(total / 12);
  const monthIndex = ((total % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  return formatContractDate(year, monthIndex, Math.min(parsed.d, lastDay));
}

/** Six months after the day this package goes out (Nate, 2026-10-21:
 *  the Buyer-Broker agreement's expiration). */
export function envelopeDatePlusSixMonths(now: Date = new Date()): string {
  return addMonthsToContractDate(envelopeDateSent(now), 6) ?? envelopeDateSent(now);
}
